import React from "react";
import { Container } from "../components";
import { PartnerCatalog } from "../data/catalog";
import { FooterV2 } from "../offering_v2/components";
import { OfferingHeader } from "../offering_v2/components";
import { PartnerConfig } from "./partner_config";
import { Spacer } from "./spacer";
import { TalkWithUsPrompt } from "./template_offering_checkout_common";
import { Helmet } from "react-helmet";
import { CatalogBlock } from "./catalog_block";
import { CatalogPartnerInfo } from "./catalog_partner_info";
import { OfferingMessageType } from "./api";
import { Divider } from "./divider";
import { useMediaQuery, useQueryString } from "@/hooks";
import { FilteredCatalog } from "@/offering_v2/components/filters/filtered_catalog";
import { FilterLocation } from "@/data/partner_offering";
import { useUTMParams } from "./utm";
import { useDisplayMerchantNameAndAddress } from "@/core/splitio";

interface FlexibleWithBlockTemplateCatalogProps {
  partnerConfig: PartnerConfig;
  partnerCatalog: PartnerCatalog;
  locationAdress?: string;
  catalogLocationFilter: React.ReactNode;
  locations: FilterLocation[];
}

export const TemplateCatalogBlock = (
  props: FlexibleWithBlockTemplateCatalogProps
) => {
  const {
    partnerConfig,
    partnerCatalog,
    locationAdress,
    catalogLocationFilter,
    locations,
  } = props;
  const {
    hubspotForm,
    hubspotForms,
    favicon,
    localeOptions,
    showTalkWithUsPrompt = true,
    talkWithUsMessage,
    instagramLink,
  } = partnerConfig;
  const { id, partner, catalogBlocks } = partnerCatalog;
  const {
    id: partnerId,
    name: partnerName,
    homepageURL,
    logo: partnerLogo,
  } = partner;

  const mq = useMediaQuery();
  const { locationID } = useQueryString();
  const { utmCampaign } = useUTMParams();
  const displayMerchantNameAndAddress = useDisplayMerchantNameAndAddress();

  const hasPartnerInfo =
    displayMerchantNameAndAddress &&
    utmCampaign !== "g-book-now" &&
    !!partnerName &&
    !!locationAdress;

  const hasDescriptionAndTitle =
    !!partnerCatalog.description && !!partnerCatalog.title;

  return (
    <>
      <Container paddingHorizontal={12}>
        <Helmet>
          <title>{partnerName} Catalog Page</title>
          <link href={favicon} rel="shortcut icon" type="image/x-icon" />
        </Helmet>
        <OfferingHeader
          homepageURL={homepageURL}
          logo={partnerLogo}
          offeringID={id}
          partnerID={partnerId}
          showTalkWithUsPrompt={showTalkWithUsPrompt}
          hubspotForm={hubspotForm}
          talkWithUsMessage={talkWithUsMessage}
          messageableType={OfferingMessageType.PartnerCatalog}
          localeOptions={localeOptions}
          hubspotForms={hubspotForms}
        />

        {hasPartnerInfo && (
          <div className="container">
            <Spacer size={48} />
            <CatalogPartnerInfo
              partnerName={partnerName}
              locationAdress={locationAdress}
            />
          </div>
        )}
      </Container>
      {hasDescriptionAndTitle && (
        <>
          <Spacer size={48} />
          <Container paddingHorizontal={12}>
            <div className="container">
              <span className="fw-bold text-size-md">
                {partnerCatalog.title}
              </span>
              <Spacer size={24} />

              <span className="text-size-md">
                <div
                  dangerouslySetInnerHTML={{
                    __html: partnerCatalog.description,
                  }}
                />
              </span>
            </div>
          </Container>
        </>
      )}
      {catalogLocationFilter && (
        <>
          {(hasPartnerInfo || hasDescriptionAndTitle) && (
            <Spacer size={mq.device === "mobile" ? 16 : 24} />
          )}
          {catalogLocationFilter}
        </>
      )}
      <Container paddingHorizontal={12}>
        <Spacer size={mq.device === "mobile" ? 16 : 48} />
        {locationID && !!catalogLocationFilter ? (
          <div className="container">
            <FilteredCatalog
              catalog={partnerCatalog}
              catalogSlug={id}
              locations={locations}
              catalogBlocks={catalogBlocks}
            />
          </div>
        ) : (
          <div className="container">
            {catalogBlocks?.map((b, ind) => (
              <div key={b.id}>
                <CatalogBlock block={b} partnerCatalog={partnerCatalog} />
                {ind !== catalogBlocks.length - 1 && (
                  <>
                    <Spacer size={mq.device === "mobile" ? 24 : 40} />
                    <Divider />
                    <Spacer size={mq.device === "mobile" ? 24 : 40} />
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <Spacer size={24} />
        {/* TALK TO US MOBILE */}
        <div className="py-1 d-flex justify-content-center fw-bold d-block d-md-none">
          {showTalkWithUsPrompt ? (
            <TalkWithUsPrompt
              partnerID={partnerId}
              hubspotForm={hubspotForm}
              messageableID={id}
              messageableType={OfferingMessageType.PartnerCatalog}
              hubspotForms={hubspotForms}
            />
          ) : (
            talkWithUsMessage && `${talkWithUsMessage}`
          )}
        </div>
        <div className="container py-4">
          <FooterV2 instagramLink={instagramLink} />
        </div>
      </Container>
    </>
  );
};
